const TZ_SUFFIX_RE = /((?:T|\s)\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)(?:Z|z|[+-]\d{2}:?\d{2})$/;

const pad = (value, size = 2) => String(value).padStart(size, "0");

const stripTimezoneSuffix = (value) => {
  if (typeof value !== "string") return value;
  return value.trim().replace(TZ_SUFFIX_RE, "$1");
};

const parseLocalDateTimeParts = (value) => {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    return {
      year: value.getFullYear(),
      month: value.getMonth() + 1,
      day: value.getDate(),
      hour: value.getHours(),
      minute: value.getMinutes(),
      second: value.getSeconds(),
      millis: value.getMilliseconds(),
    };
  }

  const text = stripTimezoneSuffix(String(value));
  const match = text.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:(?:T|\s)(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?)?$/
  );
  if (!match) return null;

  const [, year, month, day, hour = "0", minute = "0", second = "0", fraction = "0"] = match;
  const millis = Number(`${fraction.slice(0, 3)}000`.slice(0, 3));
  return {
    year: Number(year),
    month: Number(month),
    day: Number(day),
    hour: Number(hour),
    minute: Number(minute),
    second: Number(second),
    millis,
  };
};

export const parseLocalDateTime = (value) => {
  const parts = parseLocalDateTimeParts(value);
  if (!parts) return null;
  return new Date(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
    parts.millis
  );
};

export const parseSchedulerDateTime = (value) => {
  const parts = value instanceof Date
    ? {
        year: value.getUTCFullYear(),
        month: value.getUTCMonth() + 1,
        day: value.getUTCDate(),
        hour: value.getUTCHours(),
        minute: value.getUTCMinutes(),
        second: value.getUTCSeconds(),
        millis: value.getUTCMilliseconds(),
      }
    : parseLocalDateTimeParts(value);
  if (!parts) return null;
  return new Date(Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
    parts.millis
  ));
};

const schedulerDateTimeParts = (value) => {
  if (value instanceof Date) {
    return {
      year: value.getUTCFullYear(),
      month: value.getUTCMonth() + 1,
      day: value.getUTCDate(),
      hour: value.getUTCHours(),
      minute: value.getUTCMinutes(),
      second: value.getUTCSeconds(),
      millis: value.getUTCMilliseconds(),
    };
  }
  return parseLocalDateTimeParts(value);
};

export const formatLocalDate = (value) => {
  const parts = parseLocalDateTimeParts(value);
  if (!parts) return "";
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`;
};

export const formatLocalTime = (value, includeSeconds = false) => {
  const parts = parseLocalDateTimeParts(value);
  if (!parts) return "";
  const seconds = includeSeconds ? `:${pad(parts.second)}` : "";
  return `${pad(parts.hour)}:${pad(parts.minute)}${seconds}`;
};

export const formatLocalDateTime = (value, includeSeconds = true) => {
  const parts = parseLocalDateTimeParts(value);
  if (!parts) return "";
  const seconds = includeSeconds ? `:${pad(parts.second)}` : "";
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)} ${pad(parts.hour)}:${pad(parts.minute)}${seconds}`;
};

export const formatSchedulerDate = (value) => {
  const parts = schedulerDateTimeParts(value);
  if (!parts) return "";
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`;
};

export const formatSchedulerTime = (value, includeSeconds = false) => {
  const parts = schedulerDateTimeParts(value);
  if (!parts) return "";
  const seconds = includeSeconds ? `:${pad(parts.second)}` : "";
  return `${pad(parts.hour)}:${pad(parts.minute)}${seconds}`;
};

export const toLocalDateTimeString = (value, includeSeconds = true) => {
  const parts = parseLocalDateTimeParts(value);
  if (!parts) return value == null ? value : stripTimezoneSuffix(String(value));
  const seconds = includeSeconds ? `:${pad(parts.second)}` : "";
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}${seconds}`;
};

const toSchedulerDateTimeString = (value, includeSeconds = true) => {
  const parts = schedulerDateTimeParts(value);
  if (!parts) return value == null ? value : stripTimezoneSuffix(String(value));
  const seconds = includeSeconds ? `:${pad(parts.second)}` : "";
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}${seconds}`;
};

export const diffLocalDateTimeHours = (startValue, endValue) => {
  const start = parseLocalDateTimeParts(startValue);
  const end = parseLocalDateTimeParts(endValue);
  if (!start || !end) return 0;
  const startMs = Date.UTC(
    start.year,
    start.month - 1,
    start.day,
    start.hour,
    start.minute,
    start.second,
    start.millis
  );
  const endMs = Date.UTC(
    end.year,
    end.month - 1,
    end.day,
    end.hour,
    end.minute,
    end.second,
    end.millis
  );
  return (endMs - startMs) / 3600000;
};

export const diffSchedulerDateTimeHours = (startValue, endValue) => {
  const start = schedulerDateTimeParts(startValue);
  const end = schedulerDateTimeParts(endValue);
  if (!start || !end) return 0;
  const startMs = Date.UTC(
    start.year,
    start.month - 1,
    start.day,
    start.hour,
    start.minute,
    start.second,
    start.millis
  );
  const endMs = Date.UTC(
    end.year,
    end.month - 1,
    end.day,
    end.hour,
    end.minute,
    end.second,
    end.millis
  );
  return (endMs - startMs) / 3600000;
};

export const normalizeAppointmentDates = (data) => {
  if (!data) return data;
  return {
    ...data,
    startDate: data.startDate ? toSchedulerDateTimeString(data.startDate) : data.startDate,
    endDate: data.endDate ? toSchedulerDateTimeString(data.endDate) : data.endDate,
  };
};

export const parseAppointmentDates = (appointment) => {
  if (!appointment) return appointment;
  return {
    ...appointment,
    startDate: appointment.startDate ? parseSchedulerDateTime(appointment.startDate) : appointment.startDate,
    endDate: appointment.endDate ? parseSchedulerDateTime(appointment.endDate) : appointment.endDate,
  };
};
